import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Download, Eye, FileText, Mail, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const COMPANY_LOGO = 'https://customer-assets.emergentagent.com/job_swift-movers-1/artifacts/3vyram8e_Gemini_Generated_Image_tphblstphblstphb.png';

const formatMoney = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const createInvoiceMarkup = (invoice) => {
  const lineItems = (invoice.lineItems || []).map((item) => `
    <tr>
      <td>${item.description}</td>
      <td>${Number(item.quantity || 0).toLocaleString()}</td>
      <td>${formatMoney(item.unitPrice)}</td>
      <td>${formatMoney(item.total)}</td>
    </tr>
  `).join('');

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${invoice.invoiceNumber} - Swift Move and Clean</title>
        <style>
          body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; background: #f8fafc; }
          .invoice { max-width: 900px; margin: 24px auto; background: white; padding: 32px; position: relative; }
          .watermark {
            position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
            font-size: 76px; font-weight: 700; color: rgba(22, 163, 74, 0.08); transform: rotate(-24deg);
            pointer-events: none; text-transform: uppercase;
          }
          .header { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 28px; }
          .brand { display: flex; gap: 16px; align-items: center; }
          .brand img { width: 84px; height: auto; border-radius: 12px; }
          .brand h1 { margin: 0; font-size: 28px; }
          .brand p, .meta p, .customer p { margin: 4px 0; color: #475569; }
          .pill { display: inline-block; padding: 8px 12px; background: #dcfce7; color: #166534; font-weight: 700; border-radius: 999px; }
          .section-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; margin-bottom: 24px; }
          .section h2 { margin: 0 0 10px; font-size: 14px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th, td { padding: 12px; border-bottom: 1px solid #e2e8f0; text-align: left; font-size: 14px; }
          th { background: #eff6ff; color: #1e3a8a; }
          .totals { margin-top: 18px; margin-left: auto; width: 320px; }
          .totals-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; }
          .totals-row.grand { font-size: 18px; font-weight: 700; border-bottom: none; color: #0f172a; }
          .footer { margin-top: 28px; padding-top: 18px; border-top: 1px solid #e2e8f0; color: #475569; font-size: 14px; }
          @media print {
            body { background: white; }
            .invoice { margin: 0; max-width: none; box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="invoice">
          <div class="watermark">Paid In Full</div>
          <div class="header">
            <div class="brand">
              <img src="${invoice.logoUrl || COMPANY_LOGO}" alt="Swift Move and Clean logo" />
              <div>
                <h1>PAID INVOICE</h1>
                <p><strong>Swift Move and Clean</strong></p>
                <p>${invoice.companyAddress || '130 W Brunswick Dr, Columbus, IN 47201'}</p>
                <p>${invoice.companyPhone || '(812) 669-4165'} • ${invoice.companyEmail || 'info@swiftmoveandclean.com'}</p>
              </div>
            </div>
            <div class="meta">
              <p><strong>Invoice #:</strong> ${invoice.invoiceNumber}</p>
              <p><strong>Issue Date:</strong> ${formatDate(invoice.issueDate)}</p>
              <p><strong>Due Date:</strong> ${formatDate(invoice.dueDate)}</p>
              <p><strong>Payment Date:</strong> ${formatDate(invoice.paymentDate)}</p>
              <p><strong>Payment Method:</strong> ${String(invoice.paymentMethod || '').replaceAll('_', ' ')}</p>
              ${invoice.transactionId ? `<p><strong>Transaction ID:</strong> ${invoice.transactionId}</p>` : ''}
              <div style="margin-top: 12px;"><span class="pill">Paid in Full</span></div>
            </div>
          </div>

          <div class="section-grid">
            <div class="section customer">
              <h2>Bill To</h2>
              <p><strong>${invoice.customerName}</strong></p>
              <p>${invoice.customerPhone || 'Phone not provided'}</p>
              <p>${invoice.customerEmail || 'Email not provided'}</p>
              <p>${invoice.customerAddress || 'Address not provided'}</p>
            </div>
            <div class="section">
              <h2>Service Details</h2>
              <p><strong>Job Date:</strong> ${formatDate(invoice.serviceDate)}</p>
              <p><strong>Amount Paid:</strong> ${formatMoney(invoice.amountPaid)}</p>
              <p><strong>Status:</strong> ${invoice.status}</p>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>${lineItems}</tbody>
          </table>

          <div class="totals">
            <div class="totals-row"><span>Subtotal</span><span>${formatMoney(invoice.subtotal)}</span></div>
            <div class="totals-row"><span>Tax included in price</span><span>${formatMoney(invoice.taxAmount)}</span></div>
            <div class="totals-row grand"><span>Grand Total</span><span>${formatMoney(invoice.grandTotal)}</span></div>
            <div class="totals-row"><span>Total Paid</span><span>${formatMoney(invoice.amountPaid)}</span></div>
          </div>

          <div class="footer">
            <p>Thank you for choosing Swift Move and Clean. We appreciate your business.</p>
          </div>
        </div>
      </body>
    </html>
  `;
};

const downloadInvoiceHtml = (invoice) => {
  const blob = new Blob([createInvoiceMarkup(invoice)], { type: 'text/html;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${invoice.invoiceNumber}.html`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const InvoiceTab = ({ token, refreshKey = 0 }) => {
  const [invoices, setInvoices] = useState([]);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [summary, setSummary] = useState({
    invoiceCount: 0,
    totalRevenue: 0,
    dailyRevenue: 0,
    weeklyRevenue: 0,
    monthlyRevenue: 0,
    byPaymentMethod: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    customer: '',
    paymentMethod: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const apiHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.customer) params.set('customer', filters.customer);
      if (filters.paymentMethod !== 'all') params.set('payment_method', filters.paymentMethod);
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);

      const [invoiceRes, summaryRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices?${params.toString()}`, { headers: apiHeaders }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/summary/metrics?${params.toString()}`, { headers: apiHeaders }),
      ]);

      if (!invoiceRes.ok || !summaryRes.ok) {
        throw new Error('Failed to load invoices');
      }

      setInvoices(await invoiceRes.json());
      setSummary(await summaryRes.json());
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Error loading invoices');
    } finally {
      setIsLoading(false);
    }
  }, [apiHeaders, filters]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices, refreshKey]);

  const printInvoice = (invoice) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const cleanup = () => {
      window.setTimeout(() => {
        iframe.remove();
      }, 1000);
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        cleanup();
      }
    };

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      iframe.remove();
      toast.error('Unable to prepare invoice for printing.');
      return;
    }

    doc.open();
    doc.write(createInvoiceMarkup(invoice));
    doc.close();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Invoices</h2>
          <p className="text-sm text-gray-500">Track paid invoices, revenue totals, and payment method breakdowns.</p>
        </div>
        <Button variant="outline" onClick={fetchInvoices}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Filter Invoices</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <div className="xl:col-span-2">
            <Label>Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="pl-10"
                placeholder="Invoice #, customer, address..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <Label>Customer</Label>
            <Input
              placeholder="Customer name"
              value={filters.customer}
              onChange={(e) => setFilters(prev => ({ ...prev, customer: e.target.value }))}
            />
          </div>
          <div>
            <Label>Payment Method</Label>
            <Select value={filters.paymentMethod} onValueChange={(value) => setFilters(prev => ({ ...prev, paymentMethod: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="zelle">Zelle</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3 xl:col-span-5">
            <div>
              <Label>From</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-xs text-gray-500">Invoices</p><p className="mt-2 text-2xl font-bold text-slate-900">{summary.invoiceCount}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-xs text-gray-500">Today</p><p className="mt-2 text-2xl font-bold text-emerald-600">{formatMoney(summary.dailyRevenue)}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-xs text-gray-500">This Week</p><p className="mt-2 text-2xl font-bold text-blue-600">{formatMoney(summary.weeklyRevenue)}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-xs text-gray-500">This Month</p><p className="mt-2 text-2xl font-bold text-violet-600">{formatMoney(summary.monthlyRevenue)}</p></CardContent></Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Payment Method Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {Object.keys(summary.byPaymentMethod || {}).length === 0 ? (
              <p className="col-span-full text-sm text-gray-500">No invoice payments recorded yet.</p>
            ) : (
              Object.entries(summary.byPaymentMethod).map(([method, amount]) => (
                <div key={method} className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-xs uppercase tracking-wide text-gray-500">{method.replaceAll('_', ' ')}</p>
                  <p className="mt-2 text-lg font-bold text-slate-900">{formatMoney(amount)}</p>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Invoice Ledger</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="py-8 text-center text-gray-500">Loading invoices...</p>
          ) : invoices.length === 0 ? (
            <p className="py-8 text-center text-gray-500">No paid invoices generated yet.</p>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{invoice.invoiceNumber}</h3>
                        <Badge className="bg-emerald-100 text-emerald-800">{invoice.status}</Badge>
                      </div>
                      <p className="mt-1 text-sm text-slate-700">{invoice.customerName}</p>
                      <p className="text-xs text-gray-500">
                        Job date: {formatDate(invoice.serviceDate)} • Paid: {formatDate(invoice.paymentDate)} • {invoice.paymentMethod.replaceAll('_', ' ')}
                      </p>
                    </div>
                    <div className="text-left lg:text-right">
                      <p className="text-xs text-gray-500">Amount Paid</p>
                      <p className="text-xl font-bold text-emerald-600">{formatMoney(invoice.amountPaid)}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setPreviewInvoice(invoice)}>
                      <Eye className="mr-2 h-4 w-4" />
                      Preview
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => printInvoice(invoice)}>
                      <FileText className="mr-2 h-4 w-4" />
                      Print / Save PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadInvoiceHtml(invoice)}>
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (!invoice.emailDraft?.mailto) {
                          toast.error('No customer email available for this invoice.');
                          return;
                        }
                        window.location.href = invoice.emailDraft.mailto;
                      }}
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      Email Customer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Total Revenue</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-bold text-slate-900">{formatMoney(summary.totalRevenue)}</p>
          <p className="mt-2 text-sm text-gray-500">Collected from all paid invoices in the current filtered view.</p>
        </CardContent>
      </Card>

      {previewInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
          <div className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="font-semibold text-slate-900">{previewInvoice.invoiceNumber}</h3>
                <p className="text-sm text-slate-500">{previewInvoice.customerName}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => printInvoice(previewInvoice)}>
                  <FileText className="mr-2 h-4 w-4" />
                  Print / Save PDF
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setPreviewInvoice(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <iframe
              title={`Invoice preview ${previewInvoice.invoiceNumber}`}
              className="h-full w-full bg-slate-100"
              srcDoc={createInvoiceMarkup(previewInvoice)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
